import { Button, Card, Tooltip, TooltipContent, TooltipTrigger } from "@nfp-tracker/components";
import { Calendar, Check, Settings, TrendingUp, X } from "lucide-react";
import { ReactNode, useState } from "react";

interface WelcomeGuideProps {
  onDismiss: () => void;
}

interface GuideStep {
  icon: ReactNode;
  title: string;
  description: ReactNode;
}

const WelcomeGuide = ({ onDismiss }: WelcomeGuideProps) => {
  const [step, setStep] = useState(0);

  const steps: GuideStep[] = [
    {
      icon: <Check className="h-8 w-8 text-green-600" />,
      title: "Welcome to NFP Tracker!",
      description: (
        <>
          <ul className="text-left space-y-2 mb-4">
            <li className="flex items-center gap-2"><span>✅</span>Track shared cycle observations as a couple</li>
            <li className="flex items-center gap-2"><span>✅</span>Get reminders so you don't miss a day</li>
            <li className="flex items-center gap-2"><span>✅</span>No more spreadsheets</li>
          </ul>
          <div>Let's show you around in 30 seconds.</div>
        </>
      ),
    },
    {
      icon: <Calendar className="h-8 w-8" />,
      title: "Log Daily Observations",
      description: "Record bleeding, monitor readings, intercourse, symptoms, and notes. It takes just 1 minute per day.",
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "View Your Summaries",
      description: "Check your Dashboard for cycle patterns, statistics, and trends over time.",
    },
    {
      icon: <Settings className="h-8 w-8" />,
      title: "Customize & Export",
      description: "Manage notifications, optionally invite a spouse, and export your data as CSV or PDF anytime.",
    },
  ];

  const currentStep = steps[step];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onDismiss();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <Card className="max-w-md w-full p-6 space-y-6 shadow-2xl animate-scale-in">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3 text-primary">
            {currentStep.icon}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onDismiss}
                className="h-8 w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Close</TooltipContent>
          </Tooltip>
        </div>

        <div className="space-y-3">
          <h2 className="text-2xl font-bold">{currentStep.title}</h2>
          {typeof currentStep.description === "string" ? (
            <p className="text-muted-foreground">{currentStep.description}</p>
          ) : (
            <div className="text-muted-foreground">{currentStep.description}</div>
          )}
        </div>

        <div className="flex justify-center gap-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === step
                  ? "w-8 bg-primary"
                  : i < step
                  ? "w-2 bg-primary"
                  : "w-2 bg-muted"
              }`}
            />
          ))}
        </div>

        <Button 
          onClick={handleNext} 
          className="w-full h-12 text-base"
        >
          {step < steps.length - 1 ? "Next" : "Get Started"}
        </Button>
      </Card>
    </div>
  );
};

export default WelcomeGuide;
